<template>
  <v-container fluid fill-height class="ma-0 pa-1 text-14" style="background: #4d4d4d">
    <v-row no-gutters align="center" class="px-2">
      <v-tooltip top z-index="1000">
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            small
            class="mr-1"
            v-on="on"
            @click="selectAll"
          >
            <v-icon medium color="white">{{ selectIcon }}</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("SelectAll") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000">
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            small
            class="mr-1"
            v-on="on"
            @click="refreshDocuments"
          >
            <v-icon medium color="light-blue lighten-3">mdi-refresh</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Refresh") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000">
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            small
            class="mr-1"
            v-on="on"
            @click.prevent="groupBy"
          >
            <template v-if="hasGroupedItems">
              <v-badge color="red" dot overlap>
                <v-icon medium color="light-blue lighten-3">mdi-file-tree-outline</v-icon>
              </v-badge>
            </template>
            <template v-else>
              <v-icon medium color="light-blue lighten-3">mdi-file-tree-outline</v-icon>
            </template>
          </v-btn>
        </template>
        <span>{{ $t("ToGroup") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000">
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            small
            class="mr-1"
            v-on="on"
            @click.prevent="filter"
          >
            <template v-if="hasFilteredItems">
              <v-badge color="red" dot overlap>
                <v-icon medium color="light-blue lighten-3">mdi-filter</v-icon>
              </v-badge>
            </template>
            <template v-else>
              <v-icon medium color="light-blue lighten-3">mdi-filter</v-icon>
            </template>
          </v-btn>
        </template>
        <span>{{ $t("ToFilter") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000">
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            small
            class="mr-1"
            v-on="on"
            @click.prevent="sort"
          >
            <template v-if="hasSortedItems">
              <v-badge color="red" dot overlap>
                <v-icon medium color="light-blue lighten-3">mdi-sort-alphabetical-ascending</v-icon>
              </v-badge>
            </template>
            <template v-else>
              <v-icon medium color="light-blue lighten-3">mdi-sort-alphabetical-ascending</v-icon>
            </template>
          </v-btn>
        </template>
        <span>{{ $t("Sort") }}</span>
      </v-tooltip>

      <v-spacer />

      <template v-if="!isReadonlyUser">
        <v-tooltip top z-index="1000">
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              small
              class="mr-1"
              v-on="on"
              @click.prevent="validate"
            >
              <v-icon medium color="success">mdi-check-bold</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Validate") }}</span>
        </v-tooltip>

        <v-tooltip top z-index="1000">
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              small
              class="mr-1"
              v-on="on"
              @click.prevent="refuse"
            >
              <v-icon medium color="red">mdi-close-thick</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Refuse") }}</span>
        </v-tooltip>

        <v-menu bottom offset-y>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip top z-index="1000">
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  icon
                  small
                  class="mr-1"
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }"
                >
                  <v-icon medium color="teal lighten-4">mdi-share-variant</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("Distribute") }}</span>
            </v-tooltip>
          </template>
          <v-list dense>
            <v-list-item-group>
              <v-list-item @click="internalDistributeSelected" class="py-1">
                <v-list-item-icon class="mr-3">
                  <v-icon medium>mdi-account-multiple</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{ $t("DistributeInternal") }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="externalDistributeSelected" class="py-1">
                <v-list-item-icon class="mr-3">
                  <v-icon medium>mdi-email-multiple-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{ $t("DistributeExternal") }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="generateLinkSelected" class="py-1">
                <v-list-item-icon class="mr-3">
                  <v-icon medium>mdi-link</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{ $t("CopyLinkToClipboard") }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-menu>
      </template>

      <v-menu bottom offset-y>
        <template v-slot:activator="{ on: menu, attrs }">
          <v-tooltip top z-index="1000">
            <template v-slot:activator="{ on: tooltip }">
              <v-btn
                icon
                small
                class="mr-1"
                v-bind="attrs"
                v-on="{ ...tooltip, ...menu }"
                @click="loadPluginList()"
              >
                <v-icon medium color="teal lighten-4">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Tools") }}</span>
          </v-tooltip>
        </template>
        <v-list dense>
          <v-list-item-group>
            <v-list-item @click="exportSelected" v-if="!isReadonlyUser" class="py-1">
              <v-list-item-icon class="mr-3">
                <v-icon small>mdi-export</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">{{ $t("Export") }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              @click="deleteSelected"
              :class="{ 'disabled-item': !deleteAvailable() }"
              class="py-1"
            >
              <v-list-item-icon class="mr-3">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon small v-on="on">mdi-delete</v-icon>
                  </template>
                  <span v-if="!deleteAvailable()">{{ $t("Unauthorized") }}</span>
                </v-tooltip>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">{{ $t("Delete") }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              @click="editSelected"
              :class="{ 'disabled-item': !editAvailable() }"
              class="py-1"
            >
              <v-list-item-icon class="mr-3">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon small v-on="on">mdi-pencil</v-icon>
                  </template>
                  <span v-if="!editAvailable()">{{ $t("Unauthorized") }}</span>
                </v-tooltip>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">{{ $t("Edit") }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item @click="downloadSelected" class="py-1">
              <v-list-item-icon class="mr-3">
                <v-icon small>mdi-download</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">{{ $t("Download") }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-for="plugin in pluginList"
              v-bind:key="plugin.id"
              @click="loadPlugin(plugin)"
              class="py-1"
            >
              <v-list-item-icon class="mr-3">
                <v-icon small>{{ plugin.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">{{ plugin.name }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>

      <div class="text-right">
        <div class="white--text text-caption d-flex flex-column" style="line-height: 1.2;">
          <span>{{ totalAndFiltered }}</span>
          <span>{{ selectedDocuments.length }} {{ $t("Selected").toLowerCase() }}</span>
        </div>
      </div>
    </v-row>
  </v-container>
</template>

<script>
import PluginService from '@/services/PluginService';
import Plugin from '@/ecm/models/plugin/Plugin';
import AccountType from '@/ecm/variables/AccountType';
import SearchType from '@/ecm/variables/SearchType';
import Utils from '@/ecm/common/Utils';

export default {
    name: 'DocumentListToolbar',
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        totalResults: {
            type: undefined,
            required: true,
        },
        filteredResults: {
            type: Number,
            default: 0,
        },
        selectedDocuments: {
            type: Array,
            default: () => [],
        },
        canEdit: {
            type: Boolean,
            default: true,
        },
        canDelete: {
            type: Boolean,
            default: true,
        },
        hasGroupedItems: {
            type: Boolean,
            default: false,
        },
        hasSortedItems: {
            type: Boolean,
            default: false,
        },
        hasFilteredItems: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            isSelected: false,
            selectIcon: 'mdi-checkbox-blank-outline',
            pluginList: [],
        };
    },
    computed: {
        totalAndFiltered() {
            if (this.filteredResults > 0) {
                return `${this.totalResults} ${this.$t('Results')} (${
                    this.filteredResults
                } ${this.$t('Filtered').toLowerCase()})`;
            } else {
                return `${this.totalResults} ${this.$t('Results')}`;
            }
        },
        generatedLink() {
            return `${window.location.origin}/#/search/${
                SearchType.DocumentIDs
            }/${this.selectedDocuments.join()}`;
        },
        isReadonlyUser() {
            return (
                this.$store.getters['account/user'].AccountType == AccountType.Readonly
            );
        },
    },
    methods: {
        editAvailable() {
            return !this.isReadonlyUser && this.canEdit;
        },
        deleteAvailable() {
            return !this.isReadonlyUser && this.canDelete;
        },
        selectAll() {
            this.isSelected = !this.isSelected;
            this.selectIcon = this.isSelected
                ? 'mdi-check-box-outline'
                : 'mdi-checkbox-blank-outline';

            this.$emit('on-select-all-change', this.isSelected);
        },
        unmarkSelectAll() {
            this.isSelected = false;
            this.selectIcon = 'mdi-checkbox-blank-outline';
        },
        refreshDocuments() {
            this.$emit('on-refresh-click');
            if (this.isSelected) {
                this.selectAll();
            }
        },
        validate() {
            this.$emit('on-validate-click');
        },
        refuse() {
            this.$emit('on-refuse-click');
        },
        filter() {
            this.$emit('on-filter-click');
        },
        groupBy() {
            this.$emit('on-groupBy-click');
        },
        sort() {
            this.$emit('on-sort-click');
        },
        exportSelected() {
            this.$emit('on-export-selected-click');
        },
        deleteSelected() {
            if (!this.deleteAvailable()) return;
            this.$emit('on-delete-selected-click');
        },
        editSelected() {
            if (!this.editAvailable()) return;
            this.$emit('on-edit-selected-click');
        },
        downloadSelected() {
            this.$emit('on-download-selected-click');
        },
        internalDistributeSelected() {
            this.$emit('on-internal-distribute-selected-click');
        },
        externalDistributeSelected() {
            this.$emit('on-external-distribute-selected-click');
        },
        generateLinkSelected() {
            navigator.clipboard.writeText(this.generatedLink);
            Utils.toast.success(this.$t('LinkCopiedToClipboard'));
        },
        loadPlugin(plugin) {
            const objectIDs = this.selectedDocuments;
            this.$emit('on-load-plugin-click', { plugin, objectIDs });
        },
        loadPluginList() {
            if (this.selectedDocuments.length > 0) {
                PluginService.getAuthorizedList(
                    'ResultViewToolbar',
                    this.selectedDocuments
                ).then((result) => {
                    this.pluginList = result.map(function (dto) {
                        return new Plugin(dto);
                    });
                });
            } else {
                this.pluginList = [];
            }
        },
    },
};
</script>

<style>
.disabled-item {
  opacity: 0.5;
}
</style>
